/**
 * A physics relation the reader can solve for any one of its variables — F = ma,
 * KE = ½mv², P = W/t and the like.
 *
 * A relation names its variables, the unit family each is measured in, the equation as
 * textbook writes it, the rearrangement for each variable (used verbatim in the working),
 * and a `solve` function over SI values. The generic panel does the rest: unit menus,
 * conversion, the step list.
 *
 * Expressions use the variable symbols themselves (`F ÷ a`, `½ × m × v²`), so the working
 * can substitute numbers by replacing symbols — no per-tool string building.
 */

import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  fromBase,
  toBase,
  type UnitFamily,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

/** An error key a relation's `solve` returns; the widget maps it to a message. */
export interface RelationError {
  error: string;
}

export interface RelationDef<K extends string = string> {
  keys: readonly K[];
  /** The symbol shown for each variable ("F", "m", "θ"). */
  symbols: Record<K, string>;
  families: Record<K, UnitFamily>;
  /** The relation as the textbook writes it: "F = m × a". */
  equation: string;
  /** The variable the equation is already solved for. */
  solvedFor: K;
  /** Rearranged form for each variable, "m = F ÷ a". The entry for `solvedFor` is the equation. */
  rearranged: Record<K, string>;
  /**
   * A unit id in which the working shows a variable instead of the SI base — degrees for
   * an angle, so the working reads cos(30°) rather than cos(0.5236).
   */
  workingUnit?: Partial<Record<K, string>>;
  /** Solve for `unknown` from SI values of the others (the unknown's entry is ignored). */
  solve: (unknown: K, values: Record<K, number>) => number | RelationError;
}

export function isRelationError(
  value: number | RelationError
): value is RelationError {
  return typeof value === "object";
}

/** The unit id the working uses for a variable. */
export function workingUnitId<K extends string>(
  def: RelationDef<K>,
  key: K
): string {
  const family = def.families[key];
  return (
    def.workingUnit?.[key] ??
    family.units.find((unit) => unit.factor === 1)?.id ??
    family.units[0].id
  );
}

/** A variable's SI value written in the working's unit (a plain number). */
export function workingValue<K extends string>(
  def: RelationDef<K>,
  key: K,
  si: number
): number {
  return fromBase(si, def.families[key], workingUnitId(def, key));
}

/** The symbol of the unit the working uses for a variable. */
export function workingSymbol<K extends string>(
  def: RelationDef<K>,
  key: K
): string {
  return unitSymbol(def.families[key], workingUnitId(def, key));
}

/** Replace each variable symbol in an expression with its number. */
export function substituteSymbols<K extends string>(
  def: RelationDef<K>,
  expression: string,
  show: (key: K) => string
): string {
  // Longest symbols first, so a two-character symbol is not eaten by a one-character one.
  const keys = [...def.keys].sort(
    (a, b) => def.symbols[b].length - def.symbols[a].length
  );
  const pattern = new RegExp(
    keys.map((key) => escapeRegex(def.symbols[key])).join("|"),
    "g"
  );
  // Function names (cos, arcsin, …) are copied through, so a symbol such as r cannot
  // be substituted inside "arcsin".
  return expression
    .split(FUNCTION_NAME)
    .map((part, index) =>
      index % 2 === 1
        ? part
        : part.replace(pattern, (symbol) => {
            const key = keys.find(
              (candidate) => def.symbols[candidate] === symbol
            );
            return key === undefined ? symbol : show(key);
          })
    )
    .join("");
}

const FUNCTION_NAME = /(arcsin|arccos|arctan|sin|cos|tan)/;

const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;

function escapeRegex(text: string): string {
  return text.replace(REGEX_SPECIALS, "\\$&");
}

/** The right-hand side of "m = F ÷ a". */
export function rightHandSide(form: string): string {
  const at = form.indexOf("=");
  return at === -1 ? form : form.slice(at + 1).trim();
}

export interface RelationEntry {
  value: string;
  unit: string;
}

export interface RelationState<K extends string = string> {
  unknown: K;
  values: Record<K, RelationEntry>;
}

export type Evaluation<K extends string = string> =
  | { kind: "incomplete" }
  | { kind: "unreadable"; key: K }
  | { kind: "error"; error: string }
  | { kind: "ok"; value: number; all: Record<K, number> };

/** Read the typed values, convert them to SI and solve for the chosen unknown. */
export function evaluateRelation<K extends string>(
  def: RelationDef<K>,
  state: RelationState<K>
): Evaluation<K> {
  const all = {} as Record<K, number>;
  let incomplete = false;
  for (const key of def.keys) {
    if (key === state.unknown) {
      all[key] = 0;
      continue;
    }
    const entry = state.values[key];
    if (entry.value.trim() === "") {
      incomplete = true;
      continue;
    }
    const parsed = parseQuantity(entry.value);
    if (parsed === null) {
      return { kind: "unreadable", key };
    }
    all[key] = toBase(parsed, def.families[key], entry.unit);
  }
  if (incomplete) {
    return { kind: "incomplete" };
  }
  const solved = def.solve(state.unknown, all);
  if (isRelationError(solved)) {
    return { kind: "error", error: solved.error };
  }
  if (!Number.isFinite(solved)) {
    return { kind: "error", error: "not-finite" };
  }
  all[state.unknown] = solved;
  return { kind: "ok", value: solved, all };
}

/** Fresh state: the first key unknown, every variable at its default value and unit. */
export function initialRelationState<K extends string>(
  def: RelationDef<K>,
  unknown: K,
  defaults: Partial<Record<K, RelationEntry>>
): RelationState<K> {
  const values = {} as Record<K, RelationEntry>;
  for (const key of def.keys) {
    values[key] = defaults[key] ?? {
      value: "",
      unit: unitOptionsFirst(def.families[key]),
    };
  }
  return { unknown, values };
}

function unitOptionsFirst(family: UnitFamily): string {
  const visible = family.units.filter((unit) => unit.hidden !== true);
  return (
    visible.find((unit) => unit.factor === 1) ??
    visible[0] ??
    family.units[0]
  ).id;
}
